import React, { useState, Fragment } from 'react'
import TagsInput from 'react-tagsinput'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

import { withStores } from 'utils/helpers.utils'

import { createValidationSchema } from 'utils/validators.utils'
import { SIZES } from '@elo-kit/constants/general.constants'
import { AVATAR_CROP_PARAMS } from 'constants/profile.constants'
import { FILE_UPLOAD_ACCEPT_POST_FILES } from 'constants/files.constants'

import { notify } from 'libs/common/notify'
import FCheckboxField from 'ui/formik-form/FCheckboxField'
import FTextareaField from 'ui/formik-form/FTextareaField'
import FTextField from 'ui/formik-form/FTextField'

import CropImageModal from 'ui/image-croping/CropImageModal'
import { ConfirmationModal } from '@elo-kit/components/elo-ui/confirmation-modal/ConfirmationModal'
import { InfoTooltip } from '@elo-kit/components/info-tooltip/InfoTooltip'
import { EloButton } from '@elo-kit/components/elo-button/EloButton'
import { FieldError } from '@elo-kit/components/elo-ui/field-error/FieldError'
import EditableImage from '../../components/image-croping/EditableImage'

import './community-form.scss'

const KeyLabel = ({ className, classNameRemove, getTagDisplayValue, key, deleteLabel, tag }) => {
  const value = getTagDisplayValue(tag)

  return (
    <div
      {...{
        className,
        key,
      }}
    >
      <span>
        {value}
        <i className={classNameRemove} onClick={() => deleteLabel(value)} />
      </span>
    </div>
  )
}

const RenderLayout = (tagComponents, inputComponent) => (
  <Fragment>
    {tagComponents}
    {inputComponent}
  </Fragment>
)

const CommunityForm = ({
  community: c,
  communitiesStore,
  productId,
  showConfirmationModal,
  setShowConfirmationModal,
  setEditMode,
  onConfirm,
  historyPush,
  isCommunitySettings,
  communityLabelsStore,
  communityPostsStore,
  communityMemberStore,
  setIsCommunityButton = () => {},
}) => {
  const [community, setCommunity] = useState(c)
  const [editCoverModal, setEditCoverModal] = useState(false)
  const [editAvatarModal, setEditAvatarModal] = useState(false)
  const [isUserNameValid, setUserNameValid] = useState(true)

  const toggleEditCoverModal = () => setEditCoverModal(!editCoverModal)
  const toggleEditAvatarModal = () => setEditAvatarModal(!editAvatarModal)

  const fetchCommunityData = () => {
    communityPostsStore.resetData()
    communityLabelsStore.fetchList({ communityId: community?.id })
    communityPostsStore.fetchList({ communityId: community?.id })
    communityMemberStore.fetchItem(community?.id)
  }

  const handleUpdateCover = async ({ height, width, x, y }, image) => {
    if (!image) return

    const reqData = {
      productId,
      coverRemoteUrl: image,
      coverCropX: x,
      coverCropY: y,
      coverCropW: width,
      coverCropH: height,
    }

    try {
      const res = await communitiesStore.updateItem(community?.id, reqData)
      notify('success', I18n.t('react.cabinet.notific.updated'))
      if (isCommunitySettings) {
        fetchCommunityData()
      }
      setCommunity(res.data)
    } catch (error) {
      notify('error', error)
    }
  }

  const handleUpdateAvatar = async ({ height, width, x, y }, image) => {
    const reqData = {
      productId,
      ownerAttributes: {
        ...community?.owner,
        avatarRemoteUrl: image,
        avatarCropX: x,
        avatarCropY: y,
        avatarCropW: width,
        avatarCropH: height,
      },
    }

    try {
      const res = await communitiesStore.updateItem(community?.id, reqData)
      notify('success', I18n.t('react.cabinet.notific.updated'))
      if (isCommunitySettings) {
        fetchCommunityData()
      }
      setCommunity(res.data)
    } catch (error) {
      notify('error', error)
    }
  }

  const labelsProps = {
    className: 'labels__tag',
    classNameRemove: 'fas fa-times',
  }

  const initialValues = {
    name: community?.name,
    title: community?.title,
    welcomeMessage: community?.welcomeMessage,
    labels: (community?.labels || []).map(({ name }) => name),
    postsAllowed: community?.postsAllowed,
    moderatePosts: community?.moderatePosts,
    username: community?.owner?.username || '',
  }

  const validationSchema = createValidationSchema({
    name: Yup.string()
      .required(I18n.t('react.shared.validations.required'))
      .max(50, I18n.t('react.shared.comminity.validations.name_to_long')),
    title: Yup.string().max(180, I18n.t('react.shared.comminity.validations.title_to_long')),
    welcomeMessage: Yup.string().max(3000, I18n.t('react.shared.comminity.validations.welcome_message_to_long')),
    labels: Yup.array().of(
      Yup.string()
        .matches(
          /^(?!.*[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~\d]).*$/,
          I18n.t('react.shared.comminity.validations.no_numbers_and_special_characters')
        )
        .max(10, I18n.t('react.shared.comminity.validations.label_to_long'))
    ),
    username: Yup.string()
      .required(I18n.t('react.shared.validations.required'))
      .max(50, I18n.t('react.shared.comminity.validations.name_to_long')),
  })

  const validateMemberName = async (username) => {
    const resp = await communityMemberStore.validateMemberName(
      {
        username,
        discussionId: community?.id,
      },
      { skipErrorNotific: true }
    )

    return resp.success
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    const isNameValid = await validateMemberName(values.username)
    setUserNameValid(isNameValid)

    if (!isNameValid) {
      setSubmitting(false)
      return
    }

    const reqData = {
      productId,
      name: values.name,
      title: values.title,
      welcomeMessage: values.welcomeMessage,
      postsAllowed: values.postsAllowed,
      moderatePosts: values.moderatePosts,
    }

    const newLabels = values?.labels?.filter((i) => !community.labels.some(({ name }) => i === name)) || []
    const labelsToDelete = community?.labels?.filter((i) => !values.labels.some((name) => i.name === name)) || []

    if (newLabels.length > 0 || labelsToDelete.length > 0) {
      reqData.labelsAttributes = [
        ...newLabels.map((i) => ({ name: i })),
        ...labelsToDelete.map((i) => ({ ...i, _destroy: true })),
      ]
    }

    reqData.ownerAttributes = {
      ...community?.owner,
      username: values.username,
    }

    const res = await communitiesStore.updateItem(community?.id, reqData)

    if (res.success) {
      notify('success', I18n.t('react.cabinet.notific.updated'))
      if (isCommunitySettings) {
        fetchCommunityData()
      }
      setCommunity(res.data)
      setIsCommunityButton(true)
    }

    setSubmitting(false)
  }

  const toggleConfirmationModal = () => {
    setEditMode(false)
    setShowConfirmationModal(false)
    historyPush('feed')

    if (onConfirm) {
      onConfirm()
    }
  }

  const avatarUrl = (community?.owner?.avatar[0] || {}).cropped || (community?.owner?.avatar[0] || {}).original
  const coverUrl = ((community?.cover || [])[0] || {}).cropped || ((community?.cover || [])[0] || {}).original

  return (
    <div className='disscussion-form'>
      <Formik
        onSubmit={handleSubmit}
        {...{
          initialValues: initialValues,
          validationSchema,
        }}
        enableReinitialize
      >
        {({ isSubmitting, isValid, values, setFieldValue, setTouched, errors, touched, setSubmitting }) => {
          const submitDisabled = isSubmitting || !isValid
          const { name, title, welcomeMessage, postsAllowed, moderatePosts, username, labels } = values

          if (showConfirmationModal && submitDisabled) {
            toggleConfirmationModal()
          }

          return (
            <Form>
              <div className='disscussion-form__container'>
                <div className='page-section__row'>
                  <div className='page-section__column page-section__column'>
                    <div className='page-section__column-title'>{I18n.t('react.shared.comminity.settings')}</div>
                    <Field
                      name='name'
                      component={FTextField}
                      placeholder={I18n.t('react.shared.name')}
                      label={I18n.t('react.shared.comminity.name')}
                      required
                      value={name}
                    />
                    <Field
                      name='title'
                      component={FTextField}
                      tooltipId='welcome_title'
                      label={I18n.t('react.shared.comminity.welcome_title')}
                      placeholder={I18n.t('react.shared.comminity.welcome_title_placeholder')}
                      value={title}
                    />
                    <Field
                      name='welcomeMessage'
                      placeholder={I18n.t('react.shared.comminity.message_placeholder')}
                      tooltipContent={I18n.t('react.cabinet.help_icon.welcome_message.content')}
                      component={FTextareaField}
                      label={I18n.t('react.shared.comminity.message')}
                      value={welcomeMessage}
                      rows={4}
                      unsetHeight
                      onChange={(e) => {
                        setFieldValue('welcomeMessage', e.target.value)
                        setTouched({ ...touched, welcomeMessage: e.target.value !== initialValues['welcomeMessage'] })
                      }}
                    />

                    <label className='field__label'>
                      <span>{I18n.t('react.shared.comminity.labels')}</span>
                      <InfoTooltip id='labels_popover' title={I18n.t('react.cabinet.help_icon.labels.title')} />
                      {errors.labels && (
                        <div className='field__error labels-errors' style={{ float: 'right' }}>
                          {errors.labels.find((i) => Boolean(i))}
                          <i className='fas fa-exclamation-circle' />
                        </div>
                      )}
                    </label>
                    <TagsInput
                      className='labels__area'
                      inputProps={{
                        placeholder:
                          labels && !labels.length ? I18n.t('react.shared.comminity.labels_creation_placeholder') : '',
                      }}
                      onChange={(value) => {
                        setFieldValue('labels', value)
                      }}
                      renderTag={KeyLabel}
                      renderLayout={RenderLayout}
                      tagProps={{
                        ...labelsProps,
                        deleteLabel: (value) => {
                          const list = (labels || []).filter((label) => label !== value)
                          setFieldValue('labels', list)
                        },
                      }}
                      value={labels || []}
                      onlyUnique
                    />
                    <Field
                      tooltipId='posts_allowed'
                      name='postsAllowed'
                      component={FCheckboxField}
                      label={I18n.t('react.shared.comminity.members_can_create_posts')}
                      value={postsAllowed}
                      onChange={() => {
                        setFieldValue('postsAllowed', !postsAllowed)
                        if (postsAllowed) {
                          setFieldValue('moderatePosts', false)
                        }
                      }}
                      labelClasses='checkbox-field__label--without-margin'
                    />
                    <Field
                      tooltipId='post_moderation'
                      name='moderatePosts'
                      disabled={!postsAllowed}
                      component={FCheckboxField}
                      label={I18n.t('react.shared.comminity.post_moderation_allowed')}
                      value={moderatePosts}
                      onChange={() => {
                        setFieldValue('moderatePosts', !moderatePosts)
                      }}
                      labelClasses='checkbox-field__label--without-margin'
                    />
                    <EditableImage
                      imageUrl={coverUrl}
                      loadingImage={false}
                      onImageEdit={toggleEditCoverModal}
                      imageType='square'
                      inForm
                      description={I18n.t('react.shared.comminity.cover_description')}
                      label={I18n.t('react.shared.comminity.cover')}
                    />
                  </div>
                </div>
                <div className='page-section__row'>
                  <div className='page-section__column user-name-field-container'>
                    <div className='page-section__column-title'>
                      {I18n.t('react.shared.comminity.profile_settings')}
                    </div>
                    {!errors['username'] && !isUserNameValid && (
                      <FieldError errorText={I18n.t('react.shared.comminity.username_exists')} />
                    )}
                    <Field
                      name='username'
                      tooltipId='nickname'
                      component={FTextField}
                      label={I18n.t('react.shared.comminity.nickname')}
                      placeholder={I18n.t('react.shared.comminity.your_community_nickname')}
                      value={username}
                    />
                    <EditableImage
                      imageUrl={avatarUrl}
                      loadingImage={false}
                      onImageEdit={toggleEditAvatarModal}
                      imageType='round'
                      inForm
                      description={I18n.t('react.shared.comminity.avatar_description')}
                      label={I18n.t('react.shared.comminity.avatar')}
                      noImageIcon='fas fa-user'
                    />
                  </div>
                </div>

                {editAvatarModal && (
                  <CropImageModal
                    cropParams={AVATAR_CROP_PARAMS}
                    image={(community?.owner?.avatar[0] || {}).original}
                    loaderLabel={I18n.t('react.shared.avatarImage')}
                    onCrop={handleUpdateAvatar}
                    open={editAvatarModal}
                    toggleModal={toggleEditAvatarModal}
                    title={I18n.t('react.shared.edit_avatar')}
                    acceptedFiles={FILE_UPLOAD_ACCEPT_POST_FILES}
                    isCommunity
                  />
                )}
                {editCoverModal && (
                  <CropImageModal
                    cropParams={AVATAR_CROP_PARAMS}
                    image={((community?.cover || [])[0] || {}).original}
                    loaderLabel={I18n.t('react.shared.cover_image')}
                    onCrop={handleUpdateCover}
                    open={editCoverModal}
                    toggleModal={toggleEditCoverModal}
                    title={I18n.t('react.shared.edit_cover')}
                    acceptedFiles={FILE_UPLOAD_ACCEPT_POST_FILES}
                    isCommunity
                  />
                )}
              </div>

              <EloButton
                className='elo-btn blue right float-right overview-btn'
                type='submit'
                disabled={submitDisabled}
              >
                {I18n.t('react.shared.button.save')}
              </EloButton>

              {showConfirmationModal && !submitDisabled && (
                <ConfirmationModal
                  isOpen
                  submit={() => handleSubmit(values, { setSubmitting })}
                  toggle={toggleConfirmationModal}
                  title={I18n.t('react.shared.community.unsaved_modal.title')}
                  message={I18n.t('react.shared.community.unsaved_modal.message')}
                  submitButtonChild={I18n.t('react.shared.button.save_exit')}
                  cancelButtonChild={I18n.t('react.shared.button.exit_without_saving')}
                  size={SIZES.medium}
                />
              )}
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

const stores = ['communitiesStore', 'communityLabelsStore', 'communityPostsStore', 'communityMemberStore']
export default withStores(stores, CommunityForm)
